import { Card, CardHeader, CardTitle, CardBody, Chip, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Boxes } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** Flattened row: a base or addon configoption. */
interface BareMetalRow extends ServiceConfigOption {
  _addonName?: string;
}

const muted = { color: 'var(--vn-fg-muted)' } as const;
const rightHeader = (label: string) => () => (
  <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>
);
const num = (v?: number | null) => (
  <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
    {v != null ? v : <span style={muted}>—</span>}
  </span>
);

export interface BareMetalConfigTableProps {
  detail: ServiceDetail;
}

/**
 * Bare Metal Server information config table.
 *
 * Mirrors legacy `Server.BareMetalServer / Information`: a flat table of every
 * configoption (hardware specs, bandwidth, IPs) plus each addon's options.
 * Columns: Item / Type / Unit / Brand / Model / Commitment / Unit price / Total / Billing.
 */
export function BareMetalConfigTable({ detail }: BareMetalConfigTableProps) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {formatMoney(v, currency)}
    </span>
  );

  const rows: BareMetalRow[] = [
    ...(base.data?.results ?? []),
    ...(addons.data?.results ?? []).flatMap((a) =>
      (a.configoptions ?? []).map((c) => ({ ...c, _addonName: a.service_name })),
    ),
  ];

  const hasBrand = rows.some((r) => r.brand_name);
  const hasModel = rows.some((r) => r.model_name);

  const columns: ColumnDef<BareMetalRow, unknown>[] = [
    {
      id: 'name',
      header: 'Item',
      cell: ({ row }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>
          {row.original._addonName ? (
            <Badge tone="info" appearance="subtle" size="sm">
              addon: {row.original._addonName}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { id: 'type', header: 'Type', cell: ({ row }) => row.original.type || <span style={muted}>—</span> },
    { id: 'unit', header: 'Unit', cell: ({ row }) => row.original.unit || <span style={muted}>—</span> },
  ];

  if (hasBrand) {
    columns.push({
      id: 'brand',
      header: 'Brand',
      cell: ({ row }) => row.original.brand_name || <span style={muted}>—</span>,
    });
  }
  if (hasModel) {
    columns.push({
      id: 'model',
      header: 'Model',
      cell: ({ row }) => row.original.model_name || <span style={muted}>—</span>,
    });
  }

  columns.push(
    { id: 'commitment_value', header: rightHeader('Commitment'), cell: ({ row }) => num(row.original.commitment_value) },
    { id: 'unit_price', header: rightHeader('Unit price'), cell: ({ row }) => money(row.original.commitment_price_per_unit) },
    { id: 'total', header: rightHeader('Total'), cell: ({ row }) => money(row.original.commitment_price_total) },
    {
      id: 'billing',
      header: 'Billing',
      cell: ({ row }) => (
        <Chip
          appearance="subtle"
          tone={row.original.commitment_prepaid ? 'success' : 'neutral'}
          size="sm"
        >
          {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
        </Chip>
      ),
    },
  );

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => `${r.service_id}-${r.service_configoption_id}`}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="No configuration"
              description="No hardware or bandwidth configoptions found for this service."
            />
          }
        />
      </CardBody>
    </Card>
  );
}
